<template>
  <div class="user-entries-list">
    <div class="user-entries-list__entry-wrapper user-entries-list__entry-wrapper--account-details">
      <div v-if="userProfile" class="user-entries-list__entry">
        <h6 class="user-entries-list__name">Account Details</h6>
        <p class="user-entries-list__entry">{{ accountDetails.fullName }}</p>
        <p class="user-entries-list__entry">{{ accountDetails.email }}</p>
      </div>
    </div>
    <div class="user-entries-list__entry-wrapper user-entries-list__entry-wrapper--payment-details">
      <router-link
        :to="{ name: 'payment' }"
        :class="[
          'user-entries-list__edit btn btn--tertiary btn--small btn--training',
          'user-entries-list__edit--icon',
          isLoading && 'btn--loading',
        ]"
      />
      <h6 class="user-entries-list__name">Payment Details</h6>
      <div v-if="!isUsingPaymentMethodOnFile">
        <p v-if="paymentMethod === 'card'" class="user-entries-list__entry">
          <span class="user-entries-list__entry--ccvendor">{{ creditCardVendor }}</span>
          ending in
          {{ last4Digits }}
        </p>
        <p v-else class="user-entries-list__entry user-entries-list__entry--paypal">
          <PaypalIcon class="user-entries-list__entry--icon" /> Paypal
        </p>
      </div>
      <div v-else>
        <p v-if="paymentOnFileType === 'credit_card'" class="user-entries-list__entry">
          Card ending in {{ last4DigitsOnFile }}
        </p>
        <p v-else class="user-entries-list__entry user-entries-list__entry--paypal">
          <PaypalIcon class="user-entries-list__entry--icon" />
          PayPal
        </p>
      </div>
      <div v-if="!paymentMethodOnFile" class="user-entries-list__secondary">
        <h6 class="user-entries-list__name">Billing Address</h6>
        <p class="user-entries-list__entry">{{ billingName }}</p>
        <p class="user-entries-list__entry">{{ streetAddress }}</p>
        <p class="user-entries-list__entry">{{ city }}, {{ state }} {{ zip }}</p>
        <p class="user-entries-list__entry">{{ country }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import PaypalIcon from '@/assets/icons/paypal_on_file_small.svg'

export default {
  name: 'UserEntriesList',
  components: {
    PaypalIcon: { ...PaypalIcon, name: 'PaypalIcon' },
  },
  computed: {
    ...mapState({
      isLoading: state => state.app.isComponentLoading,
      isAuthenticated: state => state.auth.isAuthenticated,
      userProfile: state => state.auth.userProfile,
      paymentEntries: state => state.subscriptions.paymentEntries,
      paymentMethod: state => state.subscriptions.paymentMethod,
      paymentMethodOnFile: state => state.subscriptions.paymentMethodOnFile,
    }),
    ...mapGetters({
      recurlyToken: 'recurly/getRecurlyPricing',
    }),
    accountDetails() {
      const { first_name, last_name } = this.userProfile
      const { email } = this.userProfile
      return { email, fullName: `${first_name} ${last_name}` }
    },
    billingName() {
      const { firstName, lastName } = this.paymentEntries
      return `${firstName} ${lastName}`
    },
    billingAddress() {
      const { streetAddress, city, state, zip, country } = this.paymentEntries
      return { streetAddress, city, state, zip, country }
    },
    streetAddress() {
      const { streetAddress } = this.billingAddress
      return streetAddress
    },
    city() {
      const { city } = this.billingAddress
      return city
    },
    state() {
      const { state } = this.billingAddress
      return state
    },
    zip() {
      const { zip } = this.billingAddress
      return zip
    },
    country() {
      const { country } = this.billingAddress
      return country
    },
    last4Digits() {
      const { last4DigitsOfCreditCard } = this.paymentEntries
      return last4DigitsOfCreditCard
    },
    creditCardVendor() {
      const { creditCardVendor } = this.paymentEntries
      return creditCardVendor
    },
    last4DigitsOnFile() {
      const { payment_method_mask } = this.paymentMethodOnFile
      return payment_method_mask || ''
    },
    paymentOnFileType() {
      const { payment_method_object } = this.paymentMethodOnFile
      return payment_method_object || 'credit_card'
    },
    isUsingPaymentMethodOnFile() {
      return this.recurlyToken === 'existing_payment_method'
    },
  },
}
</script>

<style lang="scss">
.user-entries-list {
  &__edit {
    &.btn {
      position: absolute;
      right: 0;
      top: 15%;
      border: none;
      font-family: var(--font-regular);
      text-decoration: none;
      display: flex;
      align-items: center;
      color: var(--light-blue-gray);
      @include mobile {
        right: 35px;
      }
      /* file name: '@/assets/icons/pencil_icon.svg' */
      background-image: url("data:image/svg+xml,%3Csvg width='24' height='25' viewBox='0 0 24 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M17 3.5003C17.2626 3.23766 17.5744 3.02932 17.9176 2.88718C18.2608 2.74503 18.6286 2.67188 19 2.67188C19.3714 2.67187 19.7392 2.74503 20.0824 2.88718C20.4256 3.02932 20.7374 3.23766 21 3.5003C21.2626 3.76295 21.471 4.07475 21.6131 4.41791C21.7553 4.76107 21.8284 5.12887 21.8284 5.5003C21.8284 5.87174 21.7553 6.23953 21.6131 6.58269C21.471 6.92585 21.2626 7.23766 21 7.5003L7.5 21.0003L2 22.5003L3.5 17.0003L17 3.5003Z' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: center;
      background-size: 20px;
      &:hover {
        color: var(--white);
      }
    }
  }
  &__entry-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 30px;
    background: linear-gradient(
      104.99deg,
      rgba(62, 63, 73, 0.7) -13.91%,
      rgba(62, 63, 73, 0) 100.48%
    );
    border-radius: 10px;
    margin: 15px 0px;
    position: relative;
    &--account-details {
      padding: 0;
      background: none;
      margin-top: 0;
    }
    &--payment-details {
      background: none;
      border: 1px solid var(--tundora-gray);
      margin-bottom: 30px;
      @include tablet {
        margin-bottom: 40px;
      }
    }
  }
  &__name {
    font-family: var(--font-bold);
    font-size: rem(20);
    line-height: 26px;
    margin: 5px 0px;
    padding: 0;
    color: var(--white);
  }
  &__entry {
    font-family: var(--font-regular);
    color: var(--light-blue-gray);
    margin: 5px 0px;
    font-size: rem(18);
    line-height: 22px;
    &--ccvendor {
      text-transform: capitalize;
    }
    &--paypal {
      display: flex;
      align-items: center;
      .user-entries-list__entry--icon {
        margin-right: 10px;
      }
    }
  }
  &__secondary {
    margin: 25px 0 0 0;
  }
}
</style>
